import React, { useContext, useState } from 'react';
import { Button, BodyText, LabelText, Link, UtilIconClose } from '@policygenius/mortar';

import { SIGN_IN_OPTION } from '~/containers/App/constants';
import { trackClickedLink } from '~/analytics';
import { AuthContext } from '~/contexts/Auth';
import Line from '~/components/Line';

import { truncate } from './utils';

import styles from './account-indicator.module.scss';

const CHAR_LEN_FIRST_NAME = 19;
const CHAR_LEN_FULL_NAME = 29;
const CHAR_LEN_EMAIL = 40;

type CircleInitialProps = {
  iconInitial: string;
};
const CircleInitial = ({ iconInitial }: CircleInitialProps) => (
  <div className={styles['circle-initial']}>
    <LabelText size="large" color="white">
      {iconInitial}
    </LabelText>
  </div>
);

type MobileIconProps = {
  isOpen: boolean;
  iconInitial: string;
  handleCloseClick: () => void;
};
const MobileIcon = ({ isOpen, iconInitial, handleCloseClick }: MobileIconProps) => (
  <div className={styles['mobile-only']}>
    {isOpen ? (
      <div onClick={handleCloseClick}>
        <UtilIconClose className={styles['close-icon']} />
      </div>
    ) : (
      <CircleInitial iconInitial={iconInitial} />
    )}
  </div>
);

function AccountIndicator() {
  const { auth } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const { firstName, lastName, email } = auth.user;

  const fullName = [firstName, lastName].join(' ').trim();
  const truncFirstName = truncate(firstName, CHAR_LEN_FIRST_NAME);
  const isAccountHolder = !!email;

  if (!isAccountHolder) {
    return (
      <div className={styles.signInCta} data-testid="sign-in-cta">
        <Button
          onClick={() => window.location.assign(`${window.location.origin}${SIGN_IN_OPTION.url}`)}
          variant="secondary"
        >
          Sign in
        </Button>
      </div>
    );
  }

  const handleIndicatorClick = () => setIsOpen(!isOpen);
  const handleCloseClick = () => setIsOpen(false);
  const trackDashboardClick = () => trackClickedLink('dashboard');
  const trackLogoutClick = () => trackClickedLink('logout');

  const iconInitial = ((firstName && firstName[0]) || email[0] || '').toUpperCase();

  return (
    <div className={styles.container} data-testid="account-indicator-container">
      <div onClick={handleIndicatorClick} className={styles.indicator}>
        <div className={styles['desktop-only']} data-testid="indicator-circle">
          <CircleInitial iconInitial={iconInitial} />
        </div>
        <MobileIcon isOpen={isOpen} iconInitial={iconInitial} handleCloseClick={handleCloseClick} />
        <LabelText size="large" className={styles.greeting}>
          {truncFirstName ? `Hi, ${truncFirstName}` : ''}
        </LabelText>
      </div>
      {isOpen && (
        <div className={styles.menu}>
          <div className={styles['name-email']}>
            <LabelText size="large">{truncate(fullName, CHAR_LEN_FULL_NAME)}</LabelText>
            <BodyText size="small" color="secondary">
              {truncate(email, CHAR_LEN_EMAIL)}
            </BodyText>
          </div>
          <Line />
          <ul className={styles['nav-link-container']}>
            <BodyText as="li" className={styles['nav-link']}>
              <Link variant="secondary" onClick={trackDashboardClick} href="/dashboard">
                Dashboard
              </Link>
            </BodyText>
            <BodyText as="li" className={styles['nav-link']}>
              <Link variant="secondary" onClick={trackLogoutClick} href="/users/sign_out">
                Logout
              </Link>
            </BodyText>
          </ul>
        </div>
      )}
    </div>
  );
}

export default AccountIndicator;
